import { datadogRum } from '@datadog/browser-rum'
import ReactGA from 'react-ga4'
import Hotjar from '@hotjar/browser'
import useAcceptCheckoutRule from '../graphQL/mutations/acceptCheckoutRule'
import { SessionDataArgs } from '../graphQL/queries/getSession/types'
import useAppDispatch from './useAppDispatch'
import { ICart, LoadersType } from '../interfaces'
import useModal from './useModal'
import { validateIsAlcohol, validateIsReturnable } from '../utils/validations'
import { addLoader, removeLoader } from '../redux/slices/config'
import postCreateOrder from '../services/postCreateOrder'
import { generateDataDogContext } from '../utils/dataDog'
import { ICheckoutRulesConstraints } from '../graphQL/commons'
import { sendGoogleEvent } from '../utils/sendGoogleAnalytics'
import useAppSelector from './useAppSelector'
import getAnalyticsItems from '../utils/getAnalyticsItems'
import { getAnalyticsPurchasePayload } from '../utils/getAnalyticsPayload'
import { fireGTMEvent } from '../utils/fireGTMEvent'
import useOrderConfirmation from './orders/useOrderConfirmation'
import { isOrderSplitStorefront } from '../utils/orderConfirmation'

export const useCreateOrderHook = () => {
  const { closeModal } = useModal()
  const dispatch = useAppDispatch()
  const [acceptCheckoutRule] = useAcceptCheckoutRule()
  const { createAndConfirmOrder } = useOrderConfirmation()
  const state = useAppSelector((stateRedux) => stateRedux)
  const { config } = state.defaultSlice

  const CreateOrder = async ({
    sessionId,
    storeFrontName,
    phone,
    cart,
    session,
  }: {
    sessionId: string
    storeFrontName: string
    phone: string
    cart: ICart
    session: SessionDataArgs
  }) => {
    const { configuration } = session

    const isAlcoholActive =
      configuration?.checkoutRules?.cartConditionPendingUserValidation.isActive
    const isReturnableActive =
      configuration?.checkoutRules?.cartWarningCheckReturnables.isActive
    // --> checkout rules
    if (validateIsReturnable(cart) && isReturnableActive) {
      acceptCheckoutRule({
        variables: {
          storefrontName: storeFrontName,
          sessionUid: sessionId,
          ruleType: ICheckoutRulesConstraints.CART_WARNING_CHECK_RETURNABLES,
        },
      })
    }
    if (validateIsAlcohol(cart) && isAlcoholActive) {
      acceptCheckoutRule({
        variables: {
          storefrontName: storeFrontName,
          sessionUid: sessionId,
          ruleType:
            ICheckoutRulesConstraints.CART_CONDITION_PENDING_USER_VALIDATION,
        },
      })
    }
    // <-- checkout rules
    closeModal({ container: 'confirmOrder' })
    dispatch(addLoader({ name: LoadersType.CREATE_ORDER, delay: 0 }))
    const category = 'cart'
    const action = 'Create Order'
    Hotjar.event(action)
    sendGoogleEvent(category, action)
    window.fbq('trackCustom', 'CreateOrder')
    const cartAnalyticsItems = getAnalyticsItems(cart)
    const analyticsPayload = getAnalyticsPurchasePayload(
      cart,
      cartAnalyticsItems,
      session,
      config,
    )
    ReactGA.event('purchase', analyticsPayload)
    window.fbq('track', 'Purchase', analyticsPayload)
    fireGTMEvent('commerce-purchase', {
      currency: analyticsPayload.currency,
      value: analyticsPayload.value,
      items: analyticsPayload.items,
      transaction_id: analyticsPayload.transaction_id,
    })
    /*
     * The opt-in branch, and the only behavioural fork in this hook.
     *
     * Compared against literal `true` because the storefront config is remote
     * JSON with no boolean guarantee — a quoted `"false"` is truthy, and would
     * silently move a storefront onto a path whose prerequisite is a Builder
     * flow edit it has not had.
     *
     * Everything above this point is shared, including the `purchase` events.
     * They fire before the order exists, which is correct for the legacy path
     * — the flow creates it moments later, in chat — and is left alone here
     * rather than moved after `createOrder`, because moving them would change
     * the estate's analytics to fix nothing this ticket claims.
     */
    const optedIn = config?.options?.inWebviewOrderConfirmation === true
    const splitForbidsOptIn = optedIn && isOrderSplitStorefront(session)

    /*
     * An opt-in the server will refuse on every attempt, so it is declined
     * here instead.
     *
     * `createOrder` and `confirmOrder` both throw `BAD_REQUEST` outright for a
     * splitting storefront, naming `createOrders`/`confirmOrders` — which
     * cinnamon does not implement. Taking the branch anyway would break
     * checkout completely for that storefront and surface as a generic
     * creation failure naming no checkout rules, with the actual cause
     * (`inWebviewOrderConfirmation` authored beside an active order splitter)
     * visible nowhere.
     *
     * Falls through to the legacy path, which is what these storefronts use
     * today and what works. Reported rather than thrown for the reason the
     * `orderConfirmation` guards give: the shopper's checkout is worth more
     * than the authoring feedback, and this is the only place the combination
     * surfaces at all.
     */
    if (splitForbidsOptIn) {
      const context = generateDataDogContext({
        title: 'inWebviewOrderConfirmation unusable on a splitting storefront',
        extraInfo: {
          function: 'useCreateOrderHook',
          maxProduct: session?.configuration?.splitters?.order?.maxProduct,
        },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(
        new Error(
          'inWebviewOrderConfirmation is authored but the storefront splits orders; createOrder/confirmOrder would reject every attempt, so the legacy path was used',
        ),
        context,
      )
    }

    /*
     * **Nothing above this line may `await`, and the reason is in other
     * files.** The confirm dialog emits `createOrder`, `closeDialog` and
     * `placeOrderFlowNext` from one synchronous batch
     * (`utils/processActions.ts` reduces the list without awaiting `emit`), so
     * this function runs only up to its first `await` before
     * `placeOrderFlowNext` flips `isDone`. What holds `usePlaceOrderFlow`'s
     * push onto `pushAfter` back from that moment is the `CREATE_ORDER` loader
     * raised above (COM-1111): the push waits for `loading` to drop, and the
     * `finally` below drops it only after `createAndConfirmOrder` returns. The
     * screen the push lands on, `templates/OrderConfirmation.tsx`, redirects
     * to the cart when it finds `creationStatus === 'idle'` with no order
     * (COM-819).
     *
     * So the `await` that bounces a shopper is one ahead of `addLoader` —
     * awaiting either `acceptCheckoutRule` call above is the plausible one.
     * The loader is not up when `isDone` flips, the push lands at once on a
     * slice still reading `idle`, and a shopper whose order is being created
     * is bounced off the screen that is about to report it, with nothing to
     * bring them back: worse than the dead-end spinner COM-819 removed, and
     * invisible on this side of the seam. Before COM-1111 the push did not
     * wait for the loader, so the hazard reached all the way down to
     * `createAndConfirmOrder`'s own `setOrderCreating()`; an `await` between
     * `addLoader` and this branch is no longer known to bounce anyone (derived
     * from the push gate, not observed). Keep the whole-block rule anyway: the
     * pin enforces it, and relaxing it buys nothing.
     * `hooks/tests/useCreateOrderDispatchOrder.test.tsx` pins the ordering —
     * `setOrderCreating` before `placeOrderFlowNext` (COM-819), and, in its own
     * `describe`, `addLoader(CREATE_ORDER)` before `placeOrderFlowNext`
     * (COM-1111): the first does not imply the second, since `addLoader` could
     * move below the `await` without touching `setOrderCreating`. That is the
     * test to read before adding one, and it
     * carries a fixture whose cart and session enter **both** rule branches —
     * without one, an `await` on either call is unreachable from the suite
     * and the pin covers an unconditional `await` only.
     */
    if (optedIn && !splitForbidsOptIn) {
      try {
        await createAndConfirmOrder({ session, phone })
      } finally {
        /*
         * The loader has to come down here, and only here.
         *
         * `addLoader` above is matched by `removeLoader` in the legacy catch
         * alone, which works because the legacy success path navigates away
         * and takes the whole document with it. Suppress that redirect and the
         * same code leaves a full-screen loader over the confirmation screen
         * forever.
         *
         * `finally` rather than a line after the await, and it is live:
         * `createAndConfirmOrder` rejects when a rule rejection's redirect
         * throws — `navigate` is called from inside its `catch`, and
         * `replaceState` raises `SecurityError` on a cross-origin target (the
         * `%2f` slug case documented above that `navigate`). The flow has
         * already been cleared by then (COM-1358), so dropping the loader
         * leaves the shopper on the cart rather than under a full-screen
         * loader with no way out.
         * `routes/tests/checkoutRuleRejectionPushAfter.test.tsx` ("the
         * redirect itself throws") reaches it.
         *
         * Deliberately not hoisted to cover both branches: removing it on the
         * legacy path would flash the cart back at a shopper mid-navigation,
         * which is a visible change to every storefront in the estate.
         *
         * `createAndConfirmOrder` returns once the outcome is terminal or
         * being polled for — the poll continues on a timer — so this does not
         * hold the loader up for the length of a callback wait.
         */
        dispatch(removeLoader(LoadersType.CREATE_ORDER))
      }
      return
    }

    try {
      const workflowApi = process.env.REACT_APP_WORKFLOW_API || ''
      const botrunnerApi = process.env.REACT_APP_BOTRUNNER_API || ''
      await postCreateOrder({
        data: session,
        phoneNumber: phone,
        workflowApi,
        botrunnerApi,
      })
    } catch (error) {
      const context = generateDataDogContext({
        title: 'could not create order',
        extraInfo: { phone, action: 'postCreateOrder' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(error, context)
      dispatch(removeLoader(LoadersType.CREATE_ORDER))
    }
  }
  return CreateOrder
}

export default useCreateOrderHook
